import { Actions } from "../../constants/actions";
import { ChoicesControlForm } from "./choices-control-form";
import { ChoicesControlHandler } from "./choices-control-handler";
import { ChoicesControlRender } from "./choices-control-render";
import { ChoicesControlUtils, type ChoicesInstance } from "./choices-control-utils";

export class ChoicesControlListener {
    public static LANGUAGE_CHANGED_EVENT = "languageChanged";

    public static CLEAR_BUTTON_SELECTOR = `button[data-action="${Actions.CHOICES_CLEAR_ALL}"]`;

    public static teardown(select: HTMLSelectElement): void {
        const internalSelect = ChoicesControlUtils.asInternalSelect(select);

        if (internalSelect.__choicesControlSyncHandler) {
            select.removeEventListener("change", internalSelect.__choicesControlSyncHandler);
            delete internalSelect.__choicesControlSyncHandler;
        }

        if (internalSelect.__choicesControlUpdateUiStateHandler) {
            select.removeEventListener("change", internalSelect.__choicesControlUpdateUiStateHandler);
            select.removeEventListener("addItem", internalSelect.__choicesControlUpdateUiStateHandler);
            select.removeEventListener("removeItem", internalSelect.__choicesControlUpdateUiStateHandler);
            delete internalSelect.__choicesControlUpdateUiStateHandler;
        }

        if (internalSelect.__choicesTranslatePlaceholderHandler) {
            document.removeEventListener(
                ChoicesControlListener.LANGUAGE_CHANGED_EVENT,
                internalSelect.__choicesTranslatePlaceholderHandler,
            );
            delete internalSelect.__choicesTranslatePlaceholderHandler;
        }

        if (internalSelect.__choicesControlClearHandler) {
            const clearButton = ChoicesControlForm.getClearButton(select);
            if (clearButton instanceof HTMLButtonElement) {
                clearButton.removeEventListener("click", internalSelect.__choicesControlClearHandler);
            }
            delete internalSelect.__choicesControlClearHandler;
        }

        if (internalSelect.__choicesControlToggleHandler) {
            const inner = ChoicesControlForm.getInner(select, internalSelect.__choicesInstance);
            if (inner instanceof HTMLElement) {
                inner.removeEventListener(
                    "click",
                    internalSelect.__choicesControlToggleHandler,
                    internalSelect.__choicesControlToggleCapture === true,
                );
            }
            delete internalSelect.__choicesControlToggleHandler;
            delete internalSelect.__choicesControlToggleCapture;
        }

        if (internalSelect.__choicesControlShowDropdownHandler) {
            select.removeEventListener("showDropdown", internalSelect.__choicesControlShowDropdownHandler);
            delete internalSelect.__choicesControlShowDropdownHandler;
        }
    }

    public static registerClearButton(select: HTMLSelectElement, instance: ChoicesInstance | null): void {
        const internalSelect = ChoicesControlUtils.asInternalSelect(select);
        const clearButton = ChoicesControlForm.getClearButton(select);
        if (!(clearButton instanceof HTMLButtonElement)) return;

        const onClear = ChoicesControlHandler.createClearHandler(select, instance);
        internalSelect.__choicesControlClearHandler = onClear;
        clearButton.addEventListener("click", onClear);
        ChoicesControlRender.moveClearButtonIntoInner(ChoicesControlForm.getInner(select, instance), clearButton);
    }

    public static registerToggle(select: HTMLSelectElement, instance: ChoicesInstance | null): void {
        const internalSelect = ChoicesControlUtils.asInternalSelect(select);
        const inner = ChoicesControlForm.getInner(select, instance);
        if (!(inner instanceof HTMLElement)) return;

        const onInnerClick = ChoicesControlHandler.createInnerClickHandler(inner, instance);
        internalSelect.__choicesControlToggleHandler = onInnerClick;
        internalSelect.__choicesControlToggleCapture = true;
        inner.addEventListener("click", onInnerClick, true);
    }

    public static registerShowDropdown(select: HTMLSelectElement, instance: ChoicesInstance | null): void {
        if (!ChoicesControlForm.isInsidePostProductPickerContainer(select)) return;

        const internalSelect = ChoicesControlUtils.asInternalSelect(select);
        const onShowDropdown = ChoicesControlHandler.createShowDropdownHandler(select, instance);
        internalSelect.__choicesControlShowDropdownHandler = onShowDropdown;
        select.addEventListener("showDropdown", onShowDropdown);
    }

    public static registerTranslatedPlaceholder(select: HTMLSelectElement, instance: ChoicesInstance | null): void {
        const internalSelect = ChoicesControlUtils.asInternalSelect(select);
        const onLanguageChanged = ChoicesControlHandler.createTranslatePlaceholderHandler(select, instance);
        internalSelect.__choicesTranslatePlaceholderHandler = onLanguageChanged;
        document.addEventListener(ChoicesControlListener.LANGUAGE_CHANGED_EVENT, onLanguageChanged);
    }

    public static registerHiddenSync(select: HTMLSelectElement, hidden: HTMLInputElement, bindFormat: string): void {
        const internalSelect = ChoicesControlUtils.asInternalSelect(select);
        const onSync = ChoicesControlHandler.createSyncHandler(select, hidden, bindFormat);
        internalSelect.__choicesControlSyncHandler = onSync;
        onSync(new Event("change"));
        select.addEventListener("change", onSync);
    }

    public static registerUiState(select: HTMLSelectElement, instance: ChoicesInstance | null): void {
        const internalSelect = ChoicesControlUtils.asInternalSelect(select);
        const onUpdate = ChoicesControlHandler.createUpdateUiStateHandler(select, instance);
        internalSelect.__choicesControlUpdateUiStateHandler = onUpdate;
        onUpdate(new Event("change"));
        select.addEventListener("change", onUpdate);
        select.addEventListener("addItem", onUpdate);
        select.addEventListener("removeItem", onUpdate);
    }
}
